import React from 'react';
import { ArrowLeft, X, ChevronRight, Scale, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from './utils';
import { useTranslation } from 'react-i18next';

const Sidebar = ({
    sidebarOpen,
    setSidebarOpen,
    navItems,
    practiceAreas,
    quickActions,
    handleQuickAction,
}) => {
    const { t } = useTranslation();
    const createIcon = (Icon, props = {}) => React.createElement(Icon, { ...props });

    return (
        <div
            className={cn(
                "fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-2xl transform transition-all duration-300 lg:relative lg:translate-x-0",
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}
        >
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                                    {createIcon(Scale, { className: "w-7 h-7 text-blue-600" })}
                                </div>
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">{t('chatbot.header.ai_assistant')}</h2>
                                <p className="text-xs opacity-90 flex items-center gap-1">
                                    {createIcon(Sparkles, { className: "w-3 h-3" })}
                                    {t('chatbot.header.ready')}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden p-2 rounded-lg hover:bg-white/20 transition-colors"
                        >
                            {createIcon(X, { className: "w-5 h-5" })}
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <div className="p-4">
                    <div className="space-y-1">
                        <Link
                            to="/"
                            className="w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 group bg-blue-500 text-white border border-blue-200 shadow-sm"
                        >
                            <ArrowLeft />
                            {t('chatbot.sidebar.back_home')}
                        </Link>
                    </div>
                </div>

                {/* Practice Areas */}
                <div className="flex-1 overflow-y-auto px-4 pb-4">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">
                        {t('chatbot.sidebar.practice_areas')}
                    </h3>
                    <div className="space-y-2">
                        {practiceAreas.map((area, index) => (
                            <div
                                key={index}
                                className="group p-3 rounded-xl bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border border-transparent hover:border-blue-300 hover:shadow-md transition-all duration-300 cursor-pointer backdrop-blur-sm"
                                onClick={() => handleQuickAction(`Tell me about ${area.title.toLowerCase()}`)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white rounded-lg shadow-sm text-blue-600 group-hover:scale-110 transition-transform">
                                        {area.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-800 text-sm">{area.title}</h4>
                                        <p className="text-xs text-gray-600 mt-0.5">{area.desc}</p>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                        {createIcon(ChevronRight, { className: "w-4 h-4 text-blue-500" })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="mt-6">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">
                            {t('chatbot.sidebar.quick_actions')}
                        </h3>
                        <div className="space-y-1">
                            {quickActions.map((action, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleQuickAction(action.query)}
                                    className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 transition-all duration-200 group text-left"
                                >
                                    <div className="text-blue-600 group-hover:scale-110 transition-transform">
                                        {action.icon}
                                    </div>
                                    <span className="text-xs font-medium text-gray-700">{action.text}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 space-y-2">
                    <div className="text-center text-xs text-gray-500">
                        {t('chatbot.sidebar.footer')}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;